/**
 * Client helpers for live market quotes — read from client cache; merge into stock rows.
 */

import {
  getLiveMarketQuotesFromClientCache,
  LIVE_MARKET_CACHE_FETCH_MS,
  LIVE_MARKET_DISPLAY_REFRESH_MS
} from './liveMarketDataCache';

export { isNyMarketOpen, isNyLiveWindow, getNyMarketSession } from './marketHours';
export {
  getLiveMarketQuotesFromClientCache,
  refreshLiveMarketClientCache,
  subscribeLiveMarketDisplay,
  notifyLiveMarketDisplayUpdate,
  startLiveMarketClientCache,
  clearLiveMarketClientCache,
  LIVE_MARKET_CLIENT_CACHE_KEY,
  LIVE_MARKET_EXTENDED_CACHE_KEY,
  LIVE_MARKET_CACHE_FETCH_MS,
  LIVE_MARKET_DISPLAY_REFRESH_MS
} from './liveMarketDataCache';

/** @deprecated use LIVE_MARKET_CACHE_FETCH_MS */
export const LIVE_MARKET_POLL_MS = LIVE_MARKET_CACHE_FETCH_MS;

const LIVE_FIELD_KEYS = [
  'close',
  'pct_change',
  'change',
  'volume',
  'low',
  'high',
  'high_prc_52weeks',
  'low_prc_52weeks',
  'market_cap',
  'sma50_prc',
  'sma200_prc',
  'open',
  'prev_close',
  'trade_timestamp'
];

/** Extended-hours (premarket/afterhours) fields merged onto stock rows. */
const EXT_FIELD_KEYS = [
  'price_ext',
  'change_ext',
  'pct_change_ext',
  'volume_ext',
  'prev_regular_close',
  'trade_timestamp_ext'
];

/**
 * Read live quotes from client cache (sync). Returns null outside the 04:00–20:00 ET
 * window unless persisted extended data exists.
 * @param {string[]} [symbolIds] - Optional filter
 * @returns {{ quotes: Object, extendedQuotes: Object, cachedAt: number, cacheTtlMs: number, available: boolean }|null}
 */
export function fetchLiveMarketQuotes(symbolIds = null) {
  return getLiveMarketQuotesFromClientCache(symbolIds);
}

/**
 * Apply live quotes to portfolio rows and recompute summary fields.
 * @param {Array} stocks
 * @param {Object} quotesBySymbolId - Regular-session live quotes
 * @param {Object} [extQuotesBySymbolId] - Extended-hours quotes (price_ext etc.)
 */
export function applyLiveQuotesToPortfolio(stocks, quotesBySymbolId, extQuotesBySymbolId = null) {
  if (!Array.isArray(stocks) || (!quotesBySymbolId && !extQuotesBySymbolId)) {
    return { stocks, summary: null };
  }

  const next = stocks.map((row) => {
    const quote = quotesBySymbolId ? quotesBySymbolId[row.symbol_id] : null;
    const extQuote = extQuotesBySymbolId ? extQuotesBySymbolId[row.symbol_id] : null;
    if (!quote && !extQuote) return row;
    const merged = applyLiveQuoteToStock(row, quote, extQuote);
    if (quote && quote.pct_change != null) merged.days_change = quote.pct_change;
    if (quote && quote.close != null && row.buy_price) {
      const bp = row.buy_price;
      merged.performance = row.actionType === 'short'
        ? 100 * (bp - quote.close) / bp
        : 100 * (quote.close - bp) / bp;
      const qty = row.qty || 1;
      merged.market_value = quote.close * qty;
    }
    return merged;
  });

  const totalCost = next.reduce((sum, s) => sum + (s.cost || 0), 0);
  const totalMarketValue = next.reduce((sum, s) => sum + (s.market_value || 0), 0);
  const safeAverage = (arr) => {
    const valid = arr.filter((v) => !isNaN(v));
    return valid.length ? valid.reduce((a, b) => a + b, 0) / valid.length : 0;
  };

  return {
    stocks: next,
    summary: {
      avg1WeekPerformance: safeAverage(next.map((s) => s.pct_change_5days || 0)),
      avg4WeekPerformance: safeAverage(next.map((s) => s.pct_change_20days || 0)),
      avgReturns: safeAverage(next.map((s) => s.performance)),
      avgPercentGain: totalCost > 0 ? 100 * (totalMarketValue - totalCost) / totalCost : 0,
      totalCost,
      totalMarketValue
    }
  };
}

/**
 * Merge live quote fields into a stock row (screener / portfolio / watchlist / detail).
 * Extended fields (price_ext, pct_change_ext, ...) persist on the row until a
 * new extended update replaces them.
 * @param {object|null} stock
 * @param {object|null} quote - Regular live fields keyed like Athena stock row
 * @param {object|null} [extQuote] - Extended-hours quote (batch-aftermarket-trade)
 * @returns {object|null}
 */
export function applyLiveQuoteToStock(stock, quote, extQuote = null) {
  if (!stock || (!quote && !extQuote)) return stock;
  const patch = {};
  if (quote) {
    for (const key of LIVE_FIELD_KEYS) {
      if (quote[key] != null && quote[key] !== '') {
        patch[key] = quote[key];
      }
    }
    if (patch.close != null) {
      patch.price = patch.close;
    }
  }
  if (extQuote) {
    for (const key of EXT_FIELD_KEYS) {
      if (extQuote[key] != null && extQuote[key] !== '') {
        patch[key] = extQuote[key];
      }
    }
    if (extQuote.session) {
      patch.ext_session = extQuote.session;
    }
  }
  return Object.keys(patch).length > 0 ? { ...stock, ...patch } : stock;
}

/**
 * Apply live quote maps to an array of stocks (by symbol_id).
 * @param {Array} stocks
 * @param {Object} quotesBySymbolId - Regular-session live quotes
 * @param {Object} [extQuotesBySymbolId] - Extended-hours quotes
 */
export function applyLiveQuotesToStocks(stocks, quotesBySymbolId, extQuotesBySymbolId = null) {
  if (!Array.isArray(stocks) || (!quotesBySymbolId && !extQuotesBySymbolId)) return stocks;
  return stocks.map((s) => {
    const id = s && (s.symbol_id || s.symbolId);
    if (!id) return s;
    return applyLiveQuoteToStock(
      s,
      quotesBySymbolId ? quotesBySymbolId[id] : null,
      extQuotesBySymbolId ? extQuotesBySymbolId[id] : null
    );
  });
}
